<template>
  <Multiselect :key="key" class="select" valueProp="id" label="text" groupLabel="text" groupOptions="children" :addOptionOn="['enter', 'tab', ';', ',', '|']" :groups="isGroup" :searchable="true" :clear-on-search="true" :mode="mode" :create-option="this.taggable" :model-value="finalValue" :options="asyncOptions" :delay="remote ? 300 : 100" :min-chars="1" :loading="loading" :on-create="handleTagCreate" @update:modelValue="handleChangeValue">
    <template v-for="(_, slotName) in $slots" v-slot:[slotName]="slotProps">
      <slot :name="slotName" v-bind="slotProps ?? {}" />
    </template>
  </Multiselect>
</template>

<script>
import Multiselect from '@vueform/multiselect'
export default {
  emits: ['changeValue', 'update:modelValue'],
  components: {
    Multiselect
  },
  data() {
    return {
      key: 0,
      isGroup: false,
      finalOptions: []
    }
  },
  computed: {
    mode() {
      return this.multiple ? 'tags' : 'single'
    },
    remote() {
      return _.isString(this.source) && String(this.source).length > 0
    },
    finalValue() {
      let value = null
      if (this.modelValue !== null) {
        value = this.modelValue
      } else if (this.value !== null) {
        value = this.value
      }

      if (this.multiple) {
        if (_.isArray(value)) {
          return value.filter((item) => /number|string/.test(typeof item) && /.+/.test(item))
        }
      } else if (/number|string/.test(typeof value) && /.+/.test(value)) {
        return value
      }

      return null
    },
    noGroupOptions() {
      if (!this.isGroup) {
        return this.finalOptions
      } else {
        return this.finalOptions.reduce((result, current) => {
          return result.concat(current.children)
        }, [])
      }
    },
    optionIds() {
      return new Set(
        this.noGroupOptions.reduce((result, current) => {
          if (current?.id) result.push(current.id)
          return result
        }, [])
      )
    }
  },
  methods: {
    async asyncOptions(query) {
      let options = this.remote ? await this.fetchData(query) : this.options
      if (this.taggable) {
        let idValues = options.map((item) => item.id)
        let diffValues = (_.isArray(this.finalValue) ? this.finalValue : [this.finalValue]).filter((x) => !idValues.includes(x))

        if (diffValues.length > 0) {
          diffValues.forEach((item) => {
            options.push({
              id: item,
              text: item
            })
          })
        }
      }
      this.isGroup = _.isObjectLike(options?.[0]?.children)
      this.finalOptions = options
      return options
    },
    fetchData(query) {
      return new Promise((resolve) => {
        let filter = Object.assign({}, this.params, {
          search: query,
          limit: 20,
          page: 1,
          field: this.field,
          init: !query ? 1 : 0,
          selected: []
        })
        if (_.isArray(this.finalValue)) {
          filter.selected = this.finalValue
        } else if (this.finalValue) {
          filter.selected = [this.finalValue]
        }
        if (filter.selected.length > 0 || !filter.init) {
          this.$util
            .forceGet(this.source, filter, false)
            .then((response) => {
              if (response?.status == 'success') {
                resolve(Object.values(response.items))
              } else {
                resolve([])
              }
            })
            .catch(() => {
              resolve([])
            })
        } else {
          resolve([])
        }
      })
    },
    handleTagCreate(option) {
      if (parseInt(this.maxInputLength) > 0) {
        if (String(option.id).length > this.maxInputLength) {
          option.id = option.id.slice(0, this.maxInputLength)
          option.text = option.text.slice(0, this.maxInputLength)
          this.$util.notify({
            text: `This element must be up to ${this.maxInputLength} characters in length`,
            status: 'warning'
          })
          return option
        }
      }
      return option
    },
    handleChangeValue(newValue) {
      if (_.isEqual(newValue, this.finalValue)) return
      let values = this.multiple ? newValue : [newValue]
      let selected = this.noGroupOptions.filter((item) => {
        return values.includes(item.id)
      })

      if (!this.multiple) {
        selected = selected.pop() || {}
        values = values.pop()
      }
      this.$emit('changeValue', values, {
        selected,
        $el: this.$el
      })
      this.$emit('update:modelValue', values)
    }
  },
  watch: {
    loading() {
      this.key++
    },
    multiple() {
      this.key++
    },
    taggable() {
      this.key++
    },
    options(newValue, oldValue) {
      if (!_.isEqual(newValue, oldValue)) {
        this.key++
      }
    },
    value(value) {
      if ((_.isArray(value) ? value : [value]).filter((item) => !this.optionIds.has(item)).length > 0) {
        this.key++
      }
    },
    modelValue(value) {
      if ((_.isArray(value) ? value : [value]).filter((item) => !this.optionIds.has(item)).length > 0) {
        this.key++
      }
    }
  },
  props: {
    modelValue: {
      type: [String, Number, Array],
      default() {
        return null
      }
    },
    value: {
      type: [String, Number, Array],
      default() {
        return null
      }
    },
    options: {
      type: Array,
      default() {
        return []
      }
    },
    loading: {
      type: Boolean,
      default() {
        return false
      }
    },
    multiple: Boolean,
    source: String,
    field: String,
    taggable: Boolean,
    maxInputLength: Number,
    params: Object
  }
}
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
<style lang="scss">
.multiselect {
  &.is-disabled {
    opacity: 0.7;
  }
}
.multiselect-tags-search {
  outline: 0 !important;
  box-shadow: 0 0 0 !important;
  border: 0 !important;
}
</style>
